import React, { useState, useEffect } from 'react';
import { Users, ArrowRight, CheckCircle, XCircle, TrendingUp, AlertCircle, GraduationCap, Award } from 'lucide-react';
import axios from 'axios';

const PromoteStudent = () => {
  const [filters, setFilters] = useState({
    course_id: '',
    session: new Date().getFullYear().toString(),
    semester_id: ''
  });
  const [courses, setCourses] = useState([]);
  const [semesters, setSemesters] = useState([]);
  const [allSemesters, setAllSemesters] = useState([]);
  const [students, setStudents] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [targetSemester, setTargetSemester] = useState(null);
  const [isLastSemester, setIsLastSemester] = useState(false);
  const [loading, setLoading] = useState(false);
  const [promoting, setPromoting] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchCourses();
    fetchSemesters();
  }, []);

  const fetchCourses = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/master-settings/courses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCourses(response.data);
    } catch (error) {
      console.error('Error fetching courses:', error);
    }
  };

  const fetchSemesters = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/master-settings/semesters`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllSemesters(response.data);
      setSemesters(response.data);
    } catch (error) {
      console.error('Error fetching semesters:', error);
    }
  };

  const handleCourseChange = (courseId) => {
    setFilters({ ...filters, course_id: courseId, semester_id: '' });
    
    if (courseId) {
      // Filter semesters by course
      const filteredSemesters = allSemesters.filter(sem => sem.course_id === courseId);
      setSemesters(filteredSemesters);
    } else {
      setSemesters(allSemesters);
    }
    setStudents([]);
    setSelectedStudents([]);
    setTargetSemester(null);
  };

  const handleSemesterChange = (semesterId) => {
    setFilters({ ...filters, semester_id: semesterId });
    
    // Auto-calculate target semester (next semester)
    if (semesterId && filters.course_id) {
      const currentSem = allSemesters.find(s => s.id === semesterId);
      if (currentSem) {
        // Find next semester for this course
        const courseSemesters = allSemesters
          .filter(s => s.course_id === filters.course_id)
          .sort((a, b) => {
            // Sort by semester number/name
            const numA = parseInt(a.name.match(/\d+/)?.[0] || 0);
            const numB = parseInt(b.name.match(/\d+/)?.[0] || 0);
            return numA - numB;
          });
        
        const currentIndex = courseSemesters.findIndex(s => s.id === semesterId);
        if (currentIndex >= 0 && currentIndex < courseSemesters.length - 1) {
          setTargetSemester(courseSemesters[currentIndex + 1]);
          setIsLastSemester(false);
        } else {
          setTargetSemester(null); // Last semester - will mark as Passout
          setIsLastSemester(true);
        }
      }
    }
    setStudents([]);
    setSelectedStudents([]);
  };

  const loadStudents = async () => {
    if (!filters.course_id || !filters.semester_id) {
      alert('Please select Course and Semester');
      return;
    }

    if (!targetSemester) {
      alert('Cannot promote from the last semester. No higher semester available.');
      return;
    }

    setLoading(true);
    try {
      // Get students
      const studentsResponse = await axios.get(`${API_URL}/api/students/dropdown`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Filter by course, semester and optionally session (if session field exists in student)
      const studentsData = Array.isArray(studentsResponse.data) ? studentsResponse.data : (studentsResponse.data?.data || []);
      const filteredStudents = studentsData.filter(
        s => s.course_id === filters.course_id && s.semester_id === filters.semester_id
      );

      // Get results and promotion history for these students
      const studentsWithResults = await Promise.all(
        filteredStudents.map(async (student) => {
          try {
            // Try to get student's latest exam results
            const resultsResponse = await axios.get(
              `${API_URL}/api/examination/student-results/${student.id}`,
              { headers: { Authorization: `Bearer ${token}` } }
            );
            
            // Check if student has been promoted before
            let promotionHistory = null;
            try {
              const promotionResponse = await axios.get(
                `${API_URL}/api/examination/student-promotion-history/${student.id}`,
                { headers: { Authorization: `Bearer ${token}` } }
              );
              promotionHistory = promotionResponse.data;
            } catch (error) {
              // No promotion history
            }
            
            return {
              ...student,
              has_results: resultsResponse.data?.results?.length > 0,
              pass_status: resultsResponse.data?.overall_status || 'Unknown',
              promotion_history: promotionHistory,
              is_promoted: promotionHistory?.total_promotions > 0,
              last_promoted_to: promotionHistory?.last_promotion?.to_semester_name
            };
          } catch (error) {
            return {
              ...student,
              has_results: false,
              pass_status: 'Unknown',
              is_promoted: false
            };
          }
        })
      );

      setStudents(studentsWithResults);
      
    } catch (error) {
      console.error('Error loading students:', error);
      alert('Failed to load students: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleStudentSelection = (studentId) => {
    if (selectedStudents.includes(studentId)) {
      setSelectedStudents(selectedStudents.filter(id => id !== studentId));
    } else {
      setSelectedStudents([...selectedStudents, studentId]);
    }
  };

  const selectAll = () => {
    if (selectedStudents.length === students.length) {
      setSelectedStudents([]);
    } else {
      setSelectedStudents(students.map(s => s.id));
    }
  };

  const handlePromote = async () => {
    if (selectedStudents.length === 0) {
      alert('Please select at least one student to promote');
      return;
    }

    if (!targetSemester && !isLastSemester) {
      alert('No target semester available for promotion');
      return;
    }

    setShowConfirmModal(true);
  };

  const confirmPromotion = async () => {
    setPromoting(true);
    try {
      if (isLastSemester) {
        // Mark as Passout instead of promoting
        const response = await axios.post(
          `${API_URL}/api/examination/mark-passout`,
          {
            student_ids: selectedStudents,
            semester_id: filters.semester_id,
            session: filters.session
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        alert(
          `🎓 Passout Successful!\n\n` +
          `Students Marked Passout: ${response.data.passout_count}\n` +
          `Semester: ${response.data.semester}\n\n` +
          `These students have completed their course!`
        );
      } else {
        // Regular promotion
        const response = await axios.post(
          `${API_URL}/api/examination/promote-students`,
          {
            student_ids: selectedStudents,
            current_semester_id: filters.semester_id,
            target_semester_id: targetSemester.id,
            session: filters.session
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        alert(
          `✅ Promotion Successful!\n\n` +
          `Promoted: ${response.data.promoted_count} students\n` +
          `From: ${response.data.from_semester}\n` +
          `To: ${response.data.to_semester}`
        );
      }
      
      setShowConfirmModal(false);
      setSelectedStudents([]);
      loadStudents(); // Reload to see updated list
      
    } catch (error) {
      console.error('Error promoting students:', error);
      alert('❌ Promotion failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setPromoting(false);
    }
  };

  const currentSemester = semesters.find(s => s.id === filters.semester_id);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Promote Students</h1>
          <p className="text-sm text-gray-500 mt-1">Promote students from one semester to another</p>
        </div>
        {selectedStudents.length > 0 && (
          <button
            onClick={handlePromote}
            className={`flex items-center gap-2 ${isLastSemester ? 'bg-green-600 hover:bg-green-700' : 'bg-blue-600 hover:bg-blue-700'} text-white px-4 py-2 rounded-lg`}
          >
            {isLastSemester ? <GraduationCap className="h-4 w-4" /> : <ArrowRight className="h-4 w-4" />}
            {isLastSemester ? `Mark ${selectedStudents.length} Passout` : `Promote ${selectedStudents.length} Student(s)`}
          </button>
        )}
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">Selection Criteria</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Course *</label>
            <select
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              value={filters.course_id}
              onChange={(e) => handleCourseChange(e.target.value)}
            >
              <option value="">Select Course</option>
              {courses.map(course => (
                <option key={course.id} value={course.id}>{course.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Session/Year *</label>
            <input
              type="text"
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              value={filters.session}
              onChange={(e) => setFilters({ ...filters, session: e.target.value })}
              placeholder="e.g., 2025"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Semester *</label>
            <select
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              value={filters.semester_id}
              onChange={(e) => handleSemesterChange(e.target.value)}
              disabled={!filters.course_id}
            >
              <option value="">{filters.course_id ? 'Select Semester' : 'Select course first'}</option>
              {semesters.map(sem => (
                <option key={sem.id} value={sem.id}>{sem.name}</option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={loadStudents}
              disabled={!filters.course_id || !filters.semester_id || (!targetSemester && !isLastSemester) || loading}
              className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
            >
              {loading ? 'Loading...' : 'Show Report'}
            </button>
          </div>
        </div>

        {currentSemester && targetSemester && (
          <div className="mt-4 p-4 bg-gradient-to-r from-blue-50 to-green-50 rounded-lg border-2 border-blue-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <TrendingUp className="h-6 w-6 text-blue-600" />
                <div>
                  <p className="text-sm text-gray-600 font-medium">Auto Promotion Path</p>
                  <p className="text-lg font-bold text-gray-900">
                    {currentSemester.name} <ArrowRight className="inline h-5 w-5 mx-2 text-green-600" /> {targetSemester.name}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-xs text-gray-500">Session/Year</p>
                <p className="text-sm font-semibold text-gray-900">{filters.session}</p>
              </div>
            </div>
          </div>
        )}

        {filters.semester_id && !targetSemester && (
          <div className="mt-4 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
            <p className="text-sm text-yellow-800 flex items-center gap-2">
              <AlertCircle className="h-4 w-4" />
              <strong>Note:</strong> This is the last semester for this course. Students cannot be promoted further.
            </p>
          </div>
        )}
      </div>

      {students.length > 0 && (
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Students ({students.length})</h3>
            <div className="flex gap-2">
              <span className="text-sm text-gray-600">
                {selectedStudents.length} selected
              </span>
              <button
                onClick={selectAll}
                className="text-sm text-blue-600 hover:text-blue-800 font-medium"
              >
                {selectedStudents.length === students.length ? 'Deselect All' : 'Select All'}
              </button>
            </div>
          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    <input
                      type="checkbox"
                      checked={selectedStudents.length === students.length && students.length > 0}
                      onChange={selectAll}
                      className="rounded"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Admission No</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Student Name</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Email</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Current Semester</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Exam Status</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Promoted</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {students.map((student) => (
                  <tr key={student.id} className={selectedStudents.includes(student.id) ? 'bg-blue-50' : ''}>
                    <td className="px-4 py-3 text-sm">
                      <input
                        type="checkbox"
                        checked={selectedStudents.includes(student.id)}
                        onChange={() => toggleStudentSelection(student.id)}
                        className="rounded"
                      />
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">
                      {student.admission_number || student.roll_number || 'N/A'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">{student.full_name}</td>
                    <td className="px-4 py-3 text-center text-sm text-gray-600">{student.email || 'N/A'}</td>
                    <td className="px-4 py-3 text-center text-sm text-gray-900">{currentSemester?.name}</td>
                    <td className="px-4 py-3 text-center">
                      {student.pass_status === 'PASS' ? (
                        <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Pass
                        </span>
                      ) : student.pass_status === 'FAIL' ? (
                        <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                          <XCircle className="h-3 w-3 mr-1" />
                          Fail
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
                          <AlertCircle className="h-3 w-3 mr-1" />
                          Unknown
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {student.is_promoted ? (
                        <div>
                          <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-800">
                            <TrendingUp className="h-3 w-3 mr-1" />
                            Yes
                          </span>
                          {student.last_promoted_to && (
                            <p className="text-[10px] text-gray-500 mt-1">
                              To: {student.last_promoted_to}
                            </p>
                          )}
                        </div>
                      ) : (
                        <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-600">
                          No
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {students.length === 0 && filters.current_semester_id && !loading && (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <Users className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">No students found for the selected criteria</p>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full m-4">
            <div className="p-6 border-b">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                {isLastSemester ? (
                  <>
                    <GraduationCap className="h-5 w-5 text-green-500" />
                    Confirm Student Passout
                  </>
                ) : (
                  <>
                    <AlertCircle className="h-5 w-5 text-orange-500" />
                    Confirm Student Promotion
                  </>
                )}
              </h3>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-gray-700">
                {isLastSemester ? (
                  <>You are about to mark <strong>{selectedStudents.length} student(s)</strong> as <strong className="text-green-600">PASSOUT</strong>:</>
                ) : (
                  <>You are about to promote <strong>{selectedStudents.length} student(s)</strong> from:</>
                )}
              </p>
              <div className={`${isLastSemester ? 'bg-green-50' : 'bg-gray-50'} p-4 rounded-lg`}>
                {isLastSemester ? (
                  <div className="text-center">
                    <Award className="h-12 w-12 text-green-500 mx-auto mb-2" />
                    <p className="text-sm text-gray-600">Final Semester</p>
                    <p className="font-semibold text-green-700">{currentSemester?.name}</p>
                    <p className="text-sm text-gray-500 mt-2">These students have completed their course! 🎓</p>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-600">Current Semester</p>
                        <p className="font-semibold text-gray-900">{currentSemester?.name}</p>
                      </div>
                      <ArrowRight className="h-6 w-6 text-green-600" />
                      <div>
                        <p className="text-sm text-gray-600">Promote to Semester (Auto)</p>
                        <p className="font-semibold text-green-700">{targetSemester?.name}</p>
                      </div>
                    </div>
                  </>
                )}
                <div className="mt-3 pt-3 border-t grid grid-cols-2 gap-2">
                  <div>
                    <p className="text-xs text-gray-500">Session/Year</p>
                    <p className="text-sm font-semibold">{filters.session}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Course</p>
                    <p className="text-sm font-semibold">{courses.find(c => c.id === filters.course_id)?.name}</p>
                  </div>
                </div>
              </div>
              <div className={`${isLastSemester ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200'} p-3 rounded-lg border`}>
                <p className={`text-sm ${isLastSemester ? 'text-green-800' : 'text-yellow-800'}`}>
                  {isLastSemester ? (
                    <><strong>🎓 Note:</strong> Students will be marked as Passout and removed from active student list.</>
                  ) : (
                    <><strong>⚠️ Note:</strong> This action will update the semester for selected students. Make sure you have backed up data if needed.</>
                  )}
                </p>
              </div>
            </div>
            <div className="p-6 border-t flex gap-2 justify-end">
              <button
                onClick={() => setShowConfirmModal(false)}
                disabled={promoting}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmPromotion}
                disabled={promoting}
                className={`px-4 py-2 ${isLastSemester ? 'bg-green-600 hover:bg-green-700' : 'bg-blue-600 hover:bg-blue-700'} text-white rounded disabled:opacity-50`}
              >
                {promoting ? (isLastSemester ? 'Marking Passout...' : 'Promoting...') : (isLastSemester ? 'Confirm & Mark Passout' : 'Confirm & Promote')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PromoteStudent;
